import React, { useContext, useMemo, useState, useEffect } from "react";
import SelectAttributes from "./configurations/SelectAttributes";
import AttributeValues from "./configurations/AttributeValues";
import Summary from "./configurations/Summary";
import { ProductConfigurationContext } from "../../../../providers/ProductConfigurationProvider";
import { Box, Button } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { HiArrowLeft } from "react-icons/hi2";
import useCreateVariant from "../../../../hooks/variants/useCreateVariant";

const steps = [
  { key: "select-attributes", element: <SelectAttributes /> },
  { key: "attribute-values", element: <AttributeValues /> },
  { key: "summary", element: <Summary /> },
];

const EditConfig = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const {
    test,
    setTest,
    selectedAttributes,
    selectedAttributeValues,
    generateVariantConfigurations,
    getFinalVariantConfigurations,
    setSelectedAttributes,
    setSelectedAttributeValues,
    setUpdatedVariantConfigurations,
  } = useContext(ProductConfigurationContext);
  const [step, setStep] = useState(0);

  // Initialize the variant creation hook
  const { handleSubmit } = useCreateVariant({
    reset: () => {
      // Navigate to products list and replace current history entry
      navigate("/catalog/products", { replace: true });
      // Reload the page after a short delay to ensure navigation completes
      setTimeout(() => {
        window.location.reload();
      }, 100);
    },
  });

  const isFirst = step === 0;
  const isLast = step === steps.length - 1;
  const CurrentView = useMemo(() => steps[step].element, [step]);

  // Get product details from route params and query params
  const searchParams = new URLSearchParams(location.search);
  const productName = searchParams.get("product_name") || "";
  const parentProductId = params.id || params.productId || ""; // Extract from route params
  const configDataParam = searchParams.get("config_data");

  // Pre-populate data for edit mode
  useEffect(() => {
    if (configDataParam) {
      try {
        const existingConfigData = JSON.parse(configDataParam);
        console.log("Pre-populating with existing config:", existingConfigData);

        // Extract unique attribute names from the existing config
        const uniqueAttributeNames = new Set();
        const selectedValuesByAttribute = {};

        existingConfigData.forEach((variant) => {
          variant.attributes.forEach((attr) => {
            uniqueAttributeNames.add(attr.attribute_name);

            // Build selected attribute values
            if (!selectedValuesByAttribute[attr.attribute_name]) {
              selectedValuesByAttribute[attr.attribute_name] = [];
            }

            const valueExists = selectedValuesByAttribute[
              attr.attribute_name
            ].some((val) => val.attribute_value_id === attr.attribute_value_id);

            if (!valueExists) {
              selectedValuesByAttribute[attr.attribute_name].push({
                attribute_value_id: attr.attribute_value_id,
                attribute_value_name: attr.attribute_value_name,
                attribute_name: attr.attribute_name,
                // We'll set the correct attribute_id when we get the full attribute list
              });
            }
          });
        });

        console.log(
          "Unique attribute names found:",
          Array.from(uniqueAttributeNames)
        );
        console.log("Selected values by attribute:", selectedValuesByAttribute);

        // Store the attribute names and values temporarily
        // The actual attribute objects will be set when the attribute list loads
        // in useSelectAttributeLogic
        window.tempPrePopulateData = {
          attributeNames: Array.from(uniqueAttributeNames),
          selectedValuesByAttribute,
          existingConfigData,
        };

        // Set selected attribute values immediately
        setSelectedAttributeValues(selectedValuesByAttribute);

        // Transform existing config to match our format and set as updated configurations
        const transformedConfig = existingConfigData.map((variant) => ({
          attributes: variant.attributes.map((attr) => ({
            attribute_value_name: attr.attribute_value_name,
            attribute_value_id: attr.attribute_value_id,
            attribute_name: attr.attribute_name,
            in_stock: true,
          })),
          images_of_product: [],
          images_of_product_preview: [],
          selling_price: variant.selling_price,
          business_selling_price: variant.business_selling_price || "",
          mrp_price: variant.mrp_price,
          quantity: "50", // Default value since not in API response
          status: variant.status ? "1" : "0",
          product_map_id: variant.id,
          product_name: variant.name,
        }));

        console.log("EditConfig - transformedConfig:", transformedConfig);
        setUpdatedVariantConfigurations(transformedConfig);
      } catch (error) {
        console.error("Error parsing config data:", error);
      }
    }
  }, [configDataParam, setUpdatedVariantConfigurations]);

  const handleBack = () => !isFirst && setStep((s) => s - 1);
  const handleNext = () => {
    if (!isLast) setStep((s) => s + 1);
    else {
      // Finish action - get final data with updates from SummaryValues
      const finalData = getFinalVariantConfigurations(productName);
      const payload = {
        parent_product_id: parentProductId,
        config: finalData,
      };

      console.log("Submitting payload:", payload);

      // Submit the payload using the create variant hook
      handleSubmit(payload);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100%",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Bottom action bar */}
      <Box
        sx={{
          bgcolor: "#f5f6f8",
          borderRadius: 1,
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          border: "1px solid",
          borderColor: "divider",
          position: "fixed",
          top: 0,
          width: "calc(100vw - 100px)",
        }}
      >
        <div>
          {/* Show browser/history back only on first step */}
          {isFirst ? (
            <button
              className="flex items-center gap-2 text-gray-600 hover:text-gray-800 text-sm px-3 py-2 rounded-md"
              onClick={() => navigate(-1)}
              title="Back to Previous Page"
            >
              <HiArrowLeft className="text-gray-600 w-6 h-6" />
              <span>Back to Previous Page</span>
            </button>
          ) : null}
        </div>

        <div className="flex gap-4">
          {/* Show step-wise back only if not first step */}
          {!isFirst && (
            <Button
              variant="outlined"
              startIcon={<ChevronLeftIcon />}
              onClick={handleBack}
              title="Back to Previous Step"
            >
              Back to Previous Step
            </Button>
          )}

          <Button
            variant="contained"
            color="error"
            endIcon={<ChevronRightIcon />}
            onClick={handleNext}
          >
            {isLast ? "Finish" : "Next"}
          </Button>
        </div>
      </Box>

      <div className=" mt-20">
        {/* Step content */}
        <Box sx={{ flexGrow: 1 }}>
          <div className="px-4">{CurrentView}</div>
        </Box>
      </div>
    </Box>
  );
};

export default EditConfig;
